/**
 * Tâche 1477 (Eric) — Relance groupée : DEUX boutons distincts.
 *
 *   1. Les documents importés dont le pipeline IA n'a jamais abouti (aucune
 *      connaissance produite) ;
 *   2. Les lots de génération de connaissances tombés en erreur.
 *
 * Les deux populations ne se recouvrent pas et ne coûtent pas la même chose :
 * un lot en erreur a déjà consommé des appels au modèle, un document jamais
 * traité non. Un seul bouton relancerait des générations déjà faites.
 * Le panneau affiche TOUJOURS ce qu'il relancerait avant qu'on clique.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ArrowsClockwise, SpinnerGap } from "@phosphor-icons/react";
import { toast } from "sonner";

import { Button } from "../ui/button";
import { API_BASE as API } from "../../lib/apiBase";
import { useLang } from "../../contexts/LanguageContext";

export default function RelanceGroupeePanel() {
  const { lang } = useLang();
  const L = (fr, en) => (lang === "fr" ? fr : en);
  const [etat, setEtat] = useState(null);
  const [enCours, setEnCours] = useState("");

  const charger = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/relance-groupee/etat`, { withCredentials: true });
      setEtat(r.data);
    } catch (_) {
      setEtat(null);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const relancer = async (quoi) => {
    setEnCours(quoi);
    try {
      const r = await axios.post(
        `${API}/api/relance-groupee/${quoi}`, {}, { withCredentials: true });
      const { relances, ignores, restants } = r.data;
      if (relances === 0) {
        toast.warning(L(
          `Rien n'a été relancé · ${ignores} élément(s) ignoré(s)`,
          `Nothing was re-run · ${ignores} item(s) skipped`));
      } else {
        toast.success(L(
          `${relances} relance(s) lancée(s)${ignores > 0 ? ` · ${ignores} ignoré(s)` : ""}${restants > 0 ? ` · ${restants} en attente du prochain clic` : ""}`,
          `${relances} re-run(s) started${ignores > 0 ? ` · ${ignores} skipped` : ""}${restants > 0 ? ` · ${restants} left for the next click` : ""}`));
      }
      await charger();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail
        : L("Relance impossible", "Re-run failed"));
    } finally {
      setEnCours("");
    }
  };

  if (!etat || (etat.nb_documents === 0 && etat.nb_lots === 0)) return null;

  return (
    <div
      className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4"
      data-testid="relance-groupee-panel"
    >
      <h3 className="text-sm font-semibold text-amber-900 flex items-center gap-2">
        <ArrowsClockwise size={16} weight="bold" />
        {L("Relance groupée", "Bulk re-run")}
      </h3>
      <p className="mt-1 text-xs text-amber-800" data-testid="relance-limite">
        {L(
          `Au plus ${etat.limite_par_clic} relances par clic (les plus récentes d'abord) : chaque relance consomme des appels au modèle.`,
          `At most ${etat.limite_par_clic} re-runs per click (most recent first): each re-run consumes model calls.`
        )}
      </p>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <div className="rounded-lg bg-white border border-amber-200 px-4 py-3">
          <p className="text-xs text-zinc-600">
            {L(
              "Documents importés dont le pipeline IA n'a jamais abouti (aucune connaissance produite).",
              "Imported documents whose AI pipeline never completed (no knowledge produced)."
            )}
          </p>
          <p className="mt-1 text-lg font-bold text-amber-800" data-testid="relance-nb-documents">
            {etat.nb_documents}
          </p>
          <Button
            size="sm"
            className="mt-2"
            disabled={etat.nb_documents === 0 || !!enCours}
            onClick={() => relancer("documents-sans-connaissance")}
            data-testid="relance-documents-btn"
          >
            {enCours === "documents-sans-connaissance" && (
              <SpinnerGap size={14} className="animate-spin mr-1" />
            )}
            {L(
              `Relancer ${Math.min(etat.nb_documents, etat.limite_par_clic)} document(s)`,
              `Re-run ${Math.min(etat.nb_documents, etat.limite_par_clic)} document(s)`
            )}
          </Button>
        </div>
        <div className="rounded-lg bg-white border border-amber-200 px-4 py-3">
          <p className="text-xs text-zinc-600">
            {L(
              "Lots de génération de connaissances (Q&R, concepts, synthèse) tombés en erreur.",
              "Knowledge generation batches (Q&A, concepts, synthesis) that failed."
            )}
          </p>
          <p className="mt-1 text-lg font-bold text-amber-800" data-testid="relance-nb-lots">
            {etat.nb_lots_rejouables}
            {etat.nb_lots > etat.nb_lots_rejouables && (
              <span
                className="ml-2 text-xs font-normal text-zinc-500"
                title={[...new Set((etat.lots_echoues || [])
                  .filter((l) => !l.rejouable).map((l) => l.raison))].join(" · ")}
                data-testid="relance-lots-non-rejouables"
              >
                {L(
                  `(${etat.nb_lots - etat.nb_lots_rejouables} non rejouable(s) en masse)`,
                  `(${etat.nb_lots - etat.nb_lots_rejouables} not re-runnable in bulk)`
                )}
              </span>
            )}
          </p>
          <Button
            size="sm"
            className="mt-2"
            disabled={etat.nb_lots_rejouables === 0 || !!enCours}
            onClick={() => relancer("lots-echoues")}
            data-testid="relance-lots-btn"
          >
            {enCours === "lots-echoues" && (
              <SpinnerGap size={14} className="animate-spin mr-1" />
            )}
            {L(
              `Rejouer ${Math.min(etat.nb_lots_rejouables, etat.limite_par_clic)} lot(s)`,
              `Replay ${Math.min(etat.nb_lots_rejouables, etat.limite_par_clic)} batch(es)`
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
